import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import React from 'react';

import { CommentContentProps, CommentForm, CommentImage } from 'shared/features/posts';
import { ViewMoreButton } from 'shared/features/posts/components/ViewMoreButton';
import { OnCommentAdd } from 'shared/hooks/useStreamChatActivity';
import { CommentEnrichedReaction } from 'shared/types';
import { MentionPosition } from 'shared/features/mentions/types';

import { EventCommentContent } from './EventCommentContent';

type EventCommentProps = Pick<
  CommentContentProps,
  'comment' | 'onLikeAdd' | 'onReactionRemove' | 'onCommentRemove'
> & {
  onCommentAdd: OnCommentAdd;
};

const sortNestedComments = (comment: CommentEnrichedReaction) =>
  comment?.latest_children?.comment?.sort(
    (a, b) => DateTime.fromISO(a.created_at).toMillis() - DateTime.fromISO(b.created_at).toMillis(),
  );

export const EventComment: React.FC<EventCommentProps> = ({
  comment,
  onCommentAdd: rawOnCommentAdd,
  onCommentRemove,
  onLikeAdd,
  onReactionRemove,
}) => {
  const [viewAllReplies, setViewAllReplies] = React.useState(false);
  const [isCommentSectionOpened, setIsCommentSectionOpened] = React.useState(false);

  const [nestedComments, setNestedComments] = React.useState(sortNestedComments(comment) || []);

  const fieldRef = React.useRef<any>();
  const formRef = React.useRef<any>();
  const [formInitialValues, setFormInitialValues] = React.useState({ comment: '' });
  const onViewAllReplies = React.useCallback(() => setViewAllReplies(true), []);

  const onChildComments = React.useCallback(
    (commentAuthorName: string | undefined) => {
      setIsCommentSectionOpened(true);
      onViewAllReplies();

      if (fieldRef.current?.scrollIntoView) {
        setTimeout(() => fieldRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' }));
      }

      if (fieldRef.current?.focus) {
        fieldRef.current.focus();
      }

      const replyCommentMessage = `${commentAuthorName}, `;
      if (formRef.current) {
        const { setFieldValue } = formRef.current;

        setFieldValue('comment', replyCommentMessage);
      } else {
        setFormInitialValues({ ...formInitialValues, comment: replyCommentMessage });
      }
    },
    [formInitialValues, onViewAllReplies],
  );

  const onCommentAdd = React.useCallback(
    async (text: string, media: CommentImage[], mentions: MentionPosition[]) => {
      const newComment = await rawOnCommentAdd({
        text,
        media,
        commentId: comment.id,
        parentEntityAuthorId: comment.user_id,
        parentEntityType: 'comment',
        mentions,
      });

      if (!newComment) {
        return;
      }

      setFormInitialValues({ ...formInitialValues, comment: '' });
      setNestedComments([...nestedComments, newComment]);
      onViewAllReplies();
    },
    [
      formInitialValues,
      rawOnCommentAdd,
      comment.id,
      comment.user_id,
      nestedComments,
      onViewAllReplies,
    ],
  );
  return (
    <React.Fragment>
      <EventCommentContent
        comment={comment}
        onCommentAdd={onChildComments}
        onCommentRemove={onCommentRemove}
        onLikeAdd={onLikeAdd}
        onReactionRemove={onReactionRemove}
      />

      <Box ml={5}>
        {!!nestedComments[0] && (
          <Box mt={1}>
            <EventCommentContent
              comment={nestedComments[0]}
              onCommentRemove={onCommentRemove}
              onCommentAdd={onChildComments}
              onLikeAdd={onLikeAdd}
              onReactionRemove={onReactionRemove}
            />
          </Box>
        )}

        {viewAllReplies &&
          nestedComments.slice(1).map(childrenComment => (
            <Box key={childrenComment.id} mt={1}>
              <EventCommentContent
                comment={childrenComment}
                onCommentAdd={onChildComments}
                onCommentRemove={onCommentRemove}
                onLikeAdd={onLikeAdd}
                onReactionRemove={onReactionRemove}
              />
            </Box>
          ))}

        {!viewAllReplies && nestedComments.length > 1 && (
          <ViewMoreButton
            onViewMore={onViewAllReplies}
            type="reply"
            amount={nestedComments.length - 1}
          />
        )}

        {isCommentSectionOpened && (
          <CommentForm
            initialValues={formInitialValues}
            formRef={formRef}
            type="reply"
            withAutoFocus
            onCommentPost={onCommentAdd}
            fieldRef={fieldRef}
          />
        )}
      </Box>
    </React.Fragment>
  );
};
